import React from 'react'
import { Box, Typography, Container } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { TrustpilotReview } from '@talentinc/gatsby-source-trustpilot-widget'
import TrustPilotStarRating from '../TrustPilot/TrustPilotStarRating'
import getColor from '../../utils/colors'
import { BGVariants } from '../../types/heroTextSection'
import useBrand from '../../hooks/useBrand'
import { useCommonStyles } from '../../utils/styles'

interface ReviewProps {
  review: TrustpilotReview
  background: BGVariants
  variableWidth?: boolean
}

const ReviewCardTitle: React.FC<ReviewProps> = ({
  review,
  background,
  variableWidth,
}) => {
  const { classes, cx } = useStyles({ background })
  const { classes: commonClasses } = useCommonStyles()
  const brand = useBrand()

  return (
    <Container
      disableGutters
      itemScope
      itemType="https://schema.org/Review"
      className={cx(classes.containerReview)}
    >
      <Box
        className={cx({
          [classes.cardContentReview]: true,
          [classes.cardContentReviewVariableWidth]: variableWidth,
        })}
      >
        <Box
          itemProp="reviewRating"
          itemScope
          itemType="https://schema.org/Rating"
        >
          <meta itemProp="worstRating" content="1" />
          <meta itemProp="ratingValue" content={review.stars.toString()} />
          <meta itemProp="bestRating" content="5" />
          <TrustPilotStarRating rating={review.stars} />
        </Box>
        <meta itemProp="datePublished" content={review.createdAt} />
        <Typography className={classes.title} variant="h4" itemProp="name">
          {review.title}
        </Typography>
        <Typography
          className={cx(classes.text, commonClasses.hideScrollbar)}
          variant={'body2'}
          itemProp="reviewBody"
        >
          {review.text}
        </Typography>
        <Typography
          className={cx({
            [classes.name]: true,
            [classes.nameTR]: brand.name !== 'ZipJob',
          })}
          variant={'body2'}
          itemProp="author"
        >
          {review.consumer.displayName}
        </Typography>
      </Box>
    </Container>
  )
}

const useStyles = makeStyles<{ background: string }>()(
  (theme, { background }) => {
    const bgColor = getColor(background, theme)

    return {
      containerReview: {
        backgroundColor: bgColor,
        borderRadius: theme.shape.borderRadius,
        margin: '1.55rem 0',
        height: '24.75rem',
        width: '95%',
      },
      cardContentReview: {
        textAlign: 'left',
        padding: '2.625rem 2rem',
        height: '100%',
      },
      stars: {
        color: theme.colors.orange['a100'],
      },
      title: {
        fontWeight: 700,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '.65rem 0 .65rem 0',
        maxWidth: '18rem',
        [theme.breakpoints.down('md')]: {
          fontSize: '1rem',
          lineHeight: 1.312,
        },
      },
      cardContentReviewVariableWidth: {
        [theme.breakpoints.down('md')]: {
          padding: 0,
          width: '250px',
        },
      },
      text: {
        maxHeight: '12.5rem',
        overflow: 'scroll',

        [theme.breakpoints.down('md')]: {
          fontSize: '1rem',
        },
      },
      name: {
        paddingTop: '.65rem',
        [theme.breakpoints.down('md')]: {
          fontSize: '1rem',
        },
      },
      nameTR: {
        color: theme.colors.gray[500],
      },
    }
  }
)

export default ReviewCardTitle
